import React, { createContext, useContext, useState, useEffect } from 'react';
import { db } from '../db';

interface CartItem {
    id: string;
    title: string;
    price: number;
    type: string;
    qty: number;
    metadata: any;
    original: any;
}

const CartContext = createContext<any>(null);

export const useCart = () => useContext(CartContext);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [items, setItems] = useState<CartItem[]>([]);
    const [products, setProducts] = useState<any[]>([]); // Master Catalog
    const [isOpen, setIsOpen] = useState(false);

    // Sync Products on Load
    useEffect(() => {
        const loadProducts = async () => {
            const cached = await db.tabData.where('tabName').equals('Master_Products').first();
            if (cached?.data) {
                setProducts(cached.data);
            }
        };
        loadProducts();
    }, []);

    const addToCart = (product: any, type = 'product', metadata = {}) => {
        setItems(prev => {
            // Check for potential duplicates if strictly single-quantity items?
            // For now, allow multiples unless ID matches
            const existing = prev.find(i => i.id === product.id && i.type === type);
            if (existing) {
                return prev.map(i => i.id === product.id ? { ...i, qty: i.qty + 1 } : i);
            }
            return [...prev, {
                id: product.Product_ID || product.id, // Handle Sheets vs Dynamic ID
                title: product.Product_Name || product.title,
                price: parseFloat(product.Price_ZAR || product.price),
                type,
                qty: 1,
                metadata, // For specialized items like Domain Name string
                original: product
            }];
        });
        setIsOpen(true); // Auto-open cart on add
    };

    const removeFromCart = (itemId: string) => {
        setItems(prev => prev.filter(i => i.id !== itemId));
    };

    const updateQty = (itemId: string, delta: number) => {
        setItems(prev => prev.map(i => {
            if (i.id === itemId) {
                const newQty = Math.max(1, i.qty + delta);
                return { ...i, qty: newQty };
            }
            return i;
        }));
    };

    const clearCart = () => setItems([]);

    const cartTotal = items.reduce((sum, item) => sum + (item.price * item.qty), 0);
    const itemCount = items.reduce((sum, item) => sum + item.qty, 0);

    return (
        <CartContext.Provider value={{
            items,
            products,
            addToCart,
            removeFromCart,
            updateQty,
            clearCart,
            cartTotal,
            itemCount,
            isOpen,
            setIsOpen
        }}>
            {children}
        </CartContext.Provider>
    );
};
